import type {
    LomdaSceneAvatar,
    SceneAvatarCorner,
    SceneAvatarTransform,
} from "../types";
import { DEFAULT_AVATAR_CORNER_SIZE } from "./constants";

/**
 * Scene canvas aspect ratio (width / height). Hardcoded to 16:9 because the
 * editor uses `aspect-video` on the scene container. Used to convert between
 * "% of width" (where size lives) and "% of height" (where y position lives)
 * so corner insets, sizes, and snapped positions stay visually proportional
 * regardless of axis.
 */
export const SCENE_ASPECT = 16 / 9;

/** Reference scene width that legacy corner+size values were authored against. */
const LEGACY_REFERENCE_WIDTH_PX = 1920;
/** Default avatar width as % of scene width. Height auto-matches via aspect-ratio CSS. */
const DEFAULT_WIDTH_PERCENT = 12.5;
/** Visual inset from edges (% of scene width). Converted to % of height when used on Y. */
const CORNER_INSET_X_PERCENT = 2;

const insetY = (xPercent: number) => xPercent * SCENE_ASPECT;

/** Avatar's height as % of scene height, given its width-% (visual square box). */
function heightPercentForWidth(widthPercent: number): number {
    return widthPercent * SCENE_ASPECT;
}

const CORNER_POSITIONS: Record<
    SceneAvatarCorner,
    (sizePercent: number) => { x: number; y: number }
> = {
    "top-left": () => ({
        x: CORNER_INSET_X_PERCENT,
        y: insetY(CORNER_INSET_X_PERCENT),
    }),
    "top-right": (s) => ({
        x: 100 - CORNER_INSET_X_PERCENT - s,
        y: insetY(CORNER_INSET_X_PERCENT),
    }),
    "bottom-left": (s) => ({
        x: CORNER_INSET_X_PERCENT,
        y: 100 - insetY(CORNER_INSET_X_PERCENT) - heightPercentForWidth(s),
    }),
    "bottom-right": (s) => ({
        x: 100 - CORNER_INSET_X_PERCENT - s,
        y: 100 - insetY(CORNER_INSET_X_PERCENT) - heightPercentForWidth(s),
    }),
};

/**
 * Resolve the effective transform for an avatar. Prefers the explicit
 * `transform` field; falls back to migrating legacy `corner` + `size` (px @
 * 1920) into % coordinates so old scenes keep rendering.
 */
export function resolveAvatarTransform(
    avatar: LomdaSceneAvatar
): SceneAvatarTransform {
    if (avatar.transform) return avatar.transform;

    const size = avatar.size ?? DEFAULT_AVATAR_CORNER_SIZE;
    const widthPercent = (size / LEGACY_REFERENCE_WIDTH_PX) * 100;
    const corner: SceneAvatarCorner = avatar.corner ?? "bottom-right";
    const { x, y } = CORNER_POSITIONS[corner](widthPercent);
    return { x, y, width: widthPercent, aspectRatio: 1 };
}

export function defaultTransform(): SceneAvatarTransform {
    return {
        x: 100 - CORNER_INSET_X_PERCENT - DEFAULT_WIDTH_PERCENT,
        y:
            100 -
            insetY(CORNER_INSET_X_PERCENT) -
            heightPercentForWidth(DEFAULT_WIDTH_PERCENT),
        width: DEFAULT_WIDTH_PERCENT,
        aspectRatio: 1,
    };
}

/**
 * Snap a transform to one of nine alignment cells (3x3). Used by the toolbar's
 * align grid for one-click positioning.
 */
export type AvatarAlignCell =
    | "top-left"
    | "top-center"
    | "top-right"
    | "middle-left"
    | "middle-center"
    | "middle-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";

export function snapToCell(
    transform: SceneAvatarTransform,
    cell: AvatarAlignCell
): SceneAvatarTransform {
    const w = transform.width;
    const h = heightPercentForWidth(w);
    const insetXp = CORNER_INSET_X_PERCENT;
    const insetYp = insetY(CORNER_INSET_X_PERCENT);

    const left = insetXp;
    const right = 100 - insetXp - w;
    const centerX = (100 - w) / 2;
    const top = insetYp;
    const bottom = 100 - insetYp - h;
    const middleY = (100 - h) / 2;

    const COORDS: Record<AvatarAlignCell, { x: number; y: number }> = {
        "top-left": { x: left, y: top },
        "top-center": { x: centerX, y: top },
        "top-right": { x: right, y: top },
        "middle-left": { x: left, y: middleY },
        "middle-center": { x: centerX, y: middleY },
        "middle-right": { x: right, y: middleY },
        "bottom-left": { x: left, y: bottom },
        "bottom-center": { x: centerX, y: bottom },
        "bottom-right": { x: right, y: bottom },
    };
    return { ...transform, ...COORDS[cell] };
}

/** Clamp transform so the avatar stays at least partially on-canvas. */
export function clampTransform(
    transform: SceneAvatarTransform
): SceneAvatarTransform {
    const w = Math.max(2, Math.min(100, transform.width));
    const h = heightPercentForWidth(w);
    return {
        ...transform,
        width: w,
        x: Math.max(-w + 5, Math.min(95, transform.x)),
        y: Math.max(-h + 5, Math.min(95, transform.y)),
    };
}
